// "Add service" during a visit (or "+1 slot" when a service runs long). Shows the ways to fit one more slot,
// best first, with what each does to other customers: nobody affected, the next customer told, their yes on
// WhatsApp (nothing moves until they agree), or a manager's approval. Options the server refused are listed with
// the reason, so staff know why (checked in, too close to their time, closing time…).
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import api, { formatTime } from '../services/api';
import { Modal, Spinner } from './ui';

const NEEDS = {
  none: { label: 'Nobody affected', tone: 'ok' },
  inform: { label: 'They are told on WhatsApp', tone: 'info' },
  consent: { label: 'Waits for their yes', tone: 'warn' },
  manager: { label: 'Manager approval', tone: 'danger' },
};
const KIND_TITLE = {
  same: 'Same stylist',
  other: 'Another stylist',
  move: 'Move the next customer',
  delay: 'Ask to come later',
  later: 'Later today',
  takeover: 'Take over the slot',
};

/**
 * booking: { id, franchise_id, lines }  purpose: 'add_service' | 'extend'  appointmentId: the line it follows
 * initialOptions: options already returned by a +1 slot clash (skips the first load)
 */
export default function AddServiceModal({ booking, purpose = 'add_service', appointmentId, initialOptions, onClose, onDone }) {
  const [data, setData] = useState(initialOptions || null);
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');
  const [soldBy, setSoldBy] = useState('');
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [staff, setStaff] = useState([]);

  const load = () =>
    api
      .addServiceOptions(booking.id, { purpose, appointment_id: appointmentId })
      .then((d) => {
        setData(d);
        setChosen('');
      })
      .catch((e) => toast.error(e.message));

  useEffect(() => {
    if (!initialOptions) load();
    if (purpose === 'add_service') {
      api
        .listServices(booking.franchise_id)
        .then((res) => setServices(res?.services || []))
        .catch(() => setServices([]));
      api
        .listStaff()
        .then((rows) => setStaff(rows.filter((s) => s.active !== false && (!booking.franchise_id || s.franchise_id === booking.franchise_id))))
        .catch(() => setStaff([]));
    }
  }, [booking.id, purpose, appointmentId]);

  const service = services.find((s) => String(s.service_id) === String(serviceId));
  const option = data?.options.find((o) => o.id === chosen);
  const ready = option && (purpose === 'extend' || service);

  const apply = async () => {
    setBusy(true);
    try {
      const res = await api.addService(booking.id, {
        option_id: option.id,
        purpose,
        appointment_id: appointmentId || data.anchor_appointment_id,
        service_id: service?.service_id || null,
        service_name: service?.name || '',
        sold_by_staff_id: soldBy ? Number(soldBy) : null,
      });
      if (res.status === 'waiting') {
        const names = (res.request?.changes || []).map((c) => c.customer_name).join(', ');
        toast.success(`Asked ${names} on WhatsApp. The service is added when they say yes.`, { duration: 6000 });
      } else {
        toast.success(purpose === 'extend' ? 'One more slot added' : 'Service added to the visit');
      }
      onDone(res);
    } catch (e) {
      toast.error(e.message);
      if (e.status === 409) load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={purpose === 'extend' ? 'One more slot' : 'Add a service to this visit'} onClose={onClose} size="wide">
      {purpose === 'add_service' ? (
        <div className="de-form-grid de-form-grid--2" style={{ marginBottom: 14 }}>
          <label className="de-field">
            <span className="de-label">Service *</span>
            <select className="de-select" value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
              <option value="">Choose from the branch’s list…</option>
              {services.map((s) => (
                <option key={s.service_id} value={s.service_id}>
                  {s.name}
                  {s.price_text ? ` · ${s.price_text}` : ''}
                </option>
              ))}
            </select>
            {data?.suggestions?.length ? (
              <div className="de-row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
                <span className="de-small de-muted">Suggested:</span>
                {data.suggestions.map((s) => (
                  <button
                    key={s.service_id}
                    type="button"
                    className={`de-chip${String(serviceId) === String(s.service_id) ? ' active' : ''}`}
                    title={`Goes well with ${s.because_of}${s.offer_text ? ` · ${s.offer_text}` : ''}`}
                    onClick={() => setServiceId(s.service_id)}
                  >
                    ✨ {s.service_name} · {s.price}
                  </button>
                ))}
              </div>
            ) : null}
          </label>
          <label className="de-field">
            <span className="de-label">Sold by (for incentives)</span>
            <select className="de-select" value={soldBy} onChange={(e) => setSoldBy(e.target.value)}>
              <option value="">The stylist of this visit</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : null}

      {!data ? (
        <div className="de-loader">
          <Spinner /> Finding options…
        </div>
      ) : (
        <>
          {data.blocked_by ? (
            <div className="de-callout" style={{ marginBottom: 12 }}>
              The stylist’s next slot is {data.blocked_by.customer_name}’s ({data.blocked_by.service} at {formatTime(data.blocked_by.from)})
              {data.blocked_by.checked_in ? ', already checked in' : ''}.
            </div>
          ) : null}
          {data.options.length ? (
            <div style={{ display: 'grid', gap: 8 }}>
              {data.options.map((o) => {
                const needs = NEEDS[o.needs] || NEEDS.none;
                const locked = o.needs === 'manager' && data.can_take_over === false; // the server checks it again
                return (
                  <label
                    key={o.id}
                    className="de-card"
                    style={{
                      padding: 12,
                      cursor: locked ? 'not-allowed' : 'pointer',
                      opacity: locked ? 0.6 : 1,
                      outline: chosen === o.id ? '2px solid var(--de-gold)' : 'none',
                      display: 'flex',
                      gap: 10,
                      alignItems: 'flex-start',
                    }}
                  >
                    <input type="radio" name="addon-option" checked={chosen === o.id} disabled={locked} onChange={() => setChosen(o.id)} style={{ marginTop: 4 }} />
                    <div style={{ flex: 1 }}>
                      <div className="de-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span className="de-td-strong">
                          {o.rank}. {KIND_TITLE[o.kind] || o.title}
                        </span>
                        <span className="de-small">
                          {o.stylist} · {formatTime(o.starts_at)} · {o.minutes} min
                        </span>
                        <span className={`de-pill de-pill--sm de-pill--${needs.tone}`}>{needs.label}</span>
                      </div>
                      <div className="de-small de-muted">{o.detail}</div>
                      {o.affected?.length ? (
                        <div className="de-small" style={{ marginTop: 4 }}>
                          {o.affected.map((a) => (
                            <div key={a.appointment_id}>
                              • {a.customer_name} ({a.service}): {formatTime(a.from)}
                              {a.to ? ` → ${formatTime(a.to)}` : ' → cancelled, offered new times'}
                              {a.to_stylist && a.to_stylist !== a.stylist ? ` with ${a.to_stylist}` : ''}
                            </div>
                          ))}
                        </div>
                      ) : null}
                      {locked ? <div className="de-small de-danger-text">Only a manager can choose this.</div> : null}
                    </div>
                  </label>
                );
              })}
            </div>
          ) : (
            <div className="de-callout">No way to fit one more slot today. Book the service for another day.</div>
          )}
          {data.unavailable?.length ? (
            <details className="de-mt-16">
              <summary className="de-small">Why some options are not offered ({data.unavailable.length})</summary>
              {data.unavailable.map((u, i) => (
                <div key={i} className="de-small de-muted" style={{ padding: '3px 0' }}>
                  <b>{KIND_TITLE[u.kind] || u.kind}:</b> {u.reason}
                </div>
              ))}
            </details>
          ) : null}
        </>
      )}

      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="de-btn de-btn--outline" onClick={load} disabled={busy}>
          Refresh options
        </button>
        <button type="button" className="de-btn de-btn--primary" disabled={!ready || busy} onClick={apply}>
          {busy ? <Spinner light /> : null}
          {option?.needs === 'consent' ? 'Ask on WhatsApp' : purpose === 'extend' ? 'Add the slot' : 'Add service'}
        </button>
      </div>
    </Modal>
  );
}
